import store from '@/store/store'
import toolsService from '@/services/amio/tools.service'
import toaster from '@/utils/toaster'

class ToolsModule {

  tools = {
    get: () => store.state.tools.list,
    set: (tools) => store.commit('TOOLS_SET', tools)
  };

  toolsLoading = {
    get: () => store.state.tools.loading,
    set: (isLoading) => store.commit('TOOLS_LOADING_SET', isLoading)
  };

  editedId = {
    get: () => store.state.tools.editedId,
    set: (id) => store.commit('TOOL_EDITED_ID_SET', id)
  };

  async fetchTools() {
    this.toolsLoading.set(true)
    try {
      this.tools.set(await toolsService.list())
    } catch (err) {
      toaster.error(`Could not load tools: ${err.message}`)
    } finally {
      this.toolsLoading.set(false)
    }
  }

  // Returns the saved tool, or null when the API rejected it — the caller keeps the form open on
  // null so the user does not lose what they typed.
  async saveTool(tool) {
    try {
      const saved = tool.id ? await toolsService.update(tool.id, toPayload(tool)) : await toolsService.create(toPayload(tool))
      // The API warns about parameters that are declared but never used in the request. It is not
      // an error — the tool is saved — so it surfaces as a warning toast rather than blocking.
      ;(saved.warnings || []).forEach(warning => toaster.warning(warning))
      await this.fetchTools()
      return saved
    } catch (err) {
      toaster.error(err.message)
      return null
    }
  }

  // Only a 409 means "still referenced" — the caller opens the usages drawer for that case alone, so
  // a timeout or a 403 does not send the user to a drawer that has nothing to do with why it failed.
  async deleteTool(id) {
    try {
      await toolsService.delete(id)
      await this.fetchTools()
      return { deleted: true }
    } catch (err) {
      toaster.error(err.message)
      return { deleted: false, status: err.response?.status }
    }
  }

  // Returns the run result, or null when the request could not even be attempted (a validation
  // error from the API). The panel shows nothing in that case — the toast carries the reason.
  async testTool(id, args, botId) {
    try {
      return await toolsService.test(id, { args, bot_id: botId || null })
    } catch (err) {
      toaster.error(err.message)
      return null
    }
  }

  // Rethrows rather than falling back to empty lists: this drawer's empty state reads as "not used
  // anywhere, safe to delete", which is the opposite of the truth when the lookup itself failed.
  async fetchReferences(id) {
    try {
      return await toolsService.getReferences(id)
    } catch (err) {
      toaster.error(`Could not load usages: ${err.message}`)
      throw err
    }
  }
}

// Only the fields the API accepts, and only those that were filled — an empty optional field is
// sent as null rather than '' so it clears rather than failing validation.
function toPayload(tool) {
  return {
    name: tool.name,
    description: tool.description,
    parameters: tool.parameters,
    url: tool.url,
    http_verb: tool.http_verb,
    headers: emptyToNull(tool.headers),
    body: tool.body || null,
    query_params: emptyToNull(tool.query_params),
    timeout_ms: tool.timeout_ms
  }
}

function emptyToNull(map) {
  if (!map || Object.keys(map).length === 0) return null
  return map
}

export default new ToolsModule()
